/**
 * The public careers page and its apply form (M24 extension).
 *
 * Everything here is anonymous, the same way browsing the storefront is -- no bearer token,
 * no storefront account. `submitApplication` writes a real `Candidate`/`Application` into
 * HR's live pipeline the moment it succeeds; there is no draft or moderation state on the
 * way there. The résumé upload is its own narrow ticket endpoint
 * (`/marketplace/careers/resume-upload-ticket`) rather than the internal
 * `@/features/files/api` one -- that one requires a session to mint a ticket at all.
 */

import { API_PREFIX, ApiError, apiFetch, apiUrl, type Envelope } from '@/api/client'

const anon = { anonymous: true } as const

export interface PublicJobPosting {
  id: string
  title: string
  department_name: string | null
  location_name: string | null
  description: string | null
  created_at: string
}

export const listCareersJobPostings = () =>
  apiFetch<Envelope<PublicJobPosting[]>>(`${API_PREFIX}/marketplace/careers/job-postings`, anon).then(
    (envelope) => envelope.data,
  )

export const getCareersJobPosting = (id: string) =>
  apiFetch<Envelope<PublicJobPosting>>(
    `${API_PREFIX}/marketplace/careers/job-postings/${id}`,
    anon,
  ).then((envelope) => envelope.data)

export interface CareerApplicationInput {
  job_posting_id: string
  full_name: string
  email: string
  phone?: string | null
  resume_key?: string | null
  notes?: string | null
}

export interface CareerApplicationResult {
  id: string
  stage: string
  applied_at: string
}

export const submitCareerApplication = (input: CareerApplicationInput) =>
  apiFetch<Envelope<CareerApplicationResult>>(`${API_PREFIX}/marketplace/careers/applications`, {
    ...anon,
    method: 'POST',
    body: JSON.stringify(input),
  }).then((envelope) => envelope.data)

interface ResumeUploadTicket {
  key: string
  url: string
  method: string
  content_type: string
  max_bytes: number
  expires_at: number
}

/** Uploads a résumé with no session and returns the storage key to send with the
 *  application -- same two-step ticket/PUT shape as `features/files/api.ts`'s
 *  `uploadFile`, kept separate because the ticket comes from a different, unauthenticated
 *  endpoint (see the module docstring). */
export async function uploadCareerResume(file: File): Promise<string> {
  const ticket = await apiFetch<Envelope<ResumeUploadTicket>>(
    `${API_PREFIX}/marketplace/careers/resume-upload-ticket`,
    {
      ...anon,
      method: 'POST',
      body: JSON.stringify({ filename: file.name, content_type: file.type || undefined }),
    },
  ).then((envelope) => envelope.data)

  if (file.size > ticket.max_bytes) {
    throw new ApiError(
      413,
      'FILE_TOO_LARGE',
      `That file is ${Math.round(file.size / 1024)} KB. The limit is ${Math.round(
        ticket.max_bytes / 1024,
      )} KB.`,
    )
  }

  const res = await fetch(apiUrl(ticket.url), {
    method: ticket.method,
    headers: { 'Content-Type': ticket.content_type },
    body: file,
  })
  if (!res.ok) {
    throw new ApiError(res.status, 'UPLOAD_FAILED', 'The résumé could not be uploaded.')
  }
  return ticket.key
}
